import React from "react";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { FaPlus } from "react-icons/fa6";
import Button from "../../../component/element/forms/Button";
import DataTable from "../../../component/element/table";
import ItemAction from "../../../component/ui/action/ItemAction";
import { Switch } from "../../../component/ui/Switch";
import CreateCompany from "../misc/CreateCompany";
import {
  delete_company,
  get_companies,
  updateCompanyStatus,
  updateCompanySubscription,
} from "../../../services/apis/companies";
import { useAppDispatch, useAppSelector } from "../../../store";
import { ICompany } from "../../../store/types/companies";

const Company: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const companies = useAppSelector((state) => state.companies.companies);
  const loading = useAppSelector((state) => state.companies.loading);
  const loadingCompanyId = useAppSelector(
    (state) => state.companies.loadingCompanyId,
  );
  const [companyToEdit, setCompanyToEdit] = React.useState<ICompany | null>(
    null,
  );
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);

  React.useEffect(() => {
    get_companies(dispatch);
  }, [dispatch]);

  const columns: ColumnDef<ICompany>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="capitalize">{getValue<string>()}</span>
      ),
    },
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "rc_number",
      header: "RC Number",
      cell: ({ getValue }) => (
        <span className="uppercase">{getValue<string>()}</span>
      ),
    },
    {
      accessorKey: "is_active",
      header: "Active",
      cell: ({ row }) => (
        <Switch
          checked={row.original.is_active}
          disabled={loadingCompanyId === row.original.id}
          aria-label={`Set ${row.original.name} active status`}
          onCheckedChange={(checked) =>
            updateCompanyStatus(row.original.id, checked, dispatch)
          }
        />
      ),
    },
    {
      accessorKey: "is_elite",
      header: "Premium",
      cell: ({ row }) => (
        <Switch
          checked={row.original.is_elite}
          disabled={loadingCompanyId === row.original.id}
          aria-label={`Set ${row.original.name} subscription status`}
          onCheckedChange={(checked) =>
            updateCompanySubscription(row.original.id, checked, dispatch)
          }
        />
      ),
    },
    {
      accessorKey: "id",
      header: "Action",
      cell: ({ row }) => (
        <ItemAction
          onView={() => navigate(`/companies/${row.original.id}/details`)}
          onEdit={() => {
            setCompanyToEdit(row.original);
            setIsCreateOpen(true);
          }}
          onDelete={() => delete_company(row.original.id, dispatch)}
          delete_loading={loadingCompanyId === row.original.id}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="w-full flex justify-between items-center">
        <h2 className="font-bold text-xl capitalize mb-0">Company</h2>
        <div className="flex items-center gap-2">
          <Button
            title="Create"
            children={<FaPlus className="mr-2" />}
            onClick={() => {
              setCompanyToEdit(null);
              setIsCreateOpen(true);
            }}
            className="px-3"
          />
        </div>
      </div>

      <div className="mt-4">
        {loading && companies.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            Loading companies...
          </div>
        ) : (
          <DataTable columns={columns} data={companies} />
        )}
      </div>

      <CreateCompany
        visible={isCreateOpen}
        onHandler={() => {
          setIsCreateOpen(false);
          setCompanyToEdit(null);
        }}
        company={companyToEdit}
      />
    </div>
  );
};

export default Company;
